import { SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, DateTools, Select, Input, Divider, RadioList } from '@treezor/design-system';

import { InfoTooltip } from '@/ui-kit/Tips/InfoTooltip';

import { SanctionEmbargoQuestionaire } from '@/features/user/components/CreateUser/SanctionEmbargoQuestionaire';
import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { SanctionEmbargoSummary } from '@/features/user/components/CreateUser/SanctionEmbargoSummary';
import { PersonalSituationSummary } from '@/features/user/components/CreateUser/PersonalSituationSummary';
import { UserContentLabel } from '@/features/user/components/contentLayout';
import { IUser } from '@/features/user/store/user.model';

import { TKycSection } from '@/features/kyc/components/userInformation/InformationSection';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { minimumDate } from '@/hooks/useDashboardConstants';

import { useUpdateUser } from './useUpdateUser';

interface IBusinessUserVerificationProps {
  user: IUser;
  isSituationOnEdition: boolean;
  isBirthAndNationalityOnEdition: boolean;
}

export function useSelfEmployedVerificationProps({
  user,
  isSituationOnEdition,
  isBirthAndNationalityOnEdition,
}: IBusinessUserVerificationProps) {
  const { t } = useTranslation();
  const config = useDashboardConfig();

  const hasPersonalSituation = config?.settings?.users?.dataQuality?.includes('personalSituation');

  const { legalAnnualTurnOverOptions, legalNetIncomeRangeOptions, legalNumberOfEmployeeRangeOptions } =
    useFieldOptions();

  const {
    onUpdateUser,
    handleValueChange,
    isLoading,
    watch,
    register,
    language,
    handleDateChange,
    handleInputChange,
    onCancelEdition,
  } = useUpdateUser(user);

  const birthdayWatch = watch('birthday');
  const selectedDate = DateTools.localStringToDate(birthdayWatch, language);
  const entitySanctionsQuestionnaireWatch = watch('entitySanctionsQuestionnaire');
  const legalAnnualTurnOverWatch = watch('legalAnnualTurnOver');
  const legalNetIncomeRangeWatch = watch('legalNetIncomeRange');
  const activityOutsideEuWatch = watch('activityOutsideEu');
  const economicSanctionsWatch = watch('economicSanctions');
  const residentCountriesSanctionsWatch = watch('residentCountriesSanctions');
  const involvedSanctionsWatch = watch('involvedSanctions');

  const { countryCodeOptions, monthlyIncomeRangeOptions, personalAssetRanges, occupationCategoryOptions } =
    useFieldOptions();

  const isBirthAndNationalityInvalid = useMemo(() => {
    return !!selectedDate && DateTools.compare(new Date(), selectedDate) === 1;
  }, [selectedDate]);

  const birthNationality: TKycSection = useMemo(
    () => ({
      subTitle: t('pages.dashboard.createUser.form.blocks.birthNationality'),
      ...(isBirthAndNationalityOnEdition
        ? {
            component: (
              <div className="flex flex-col gap-3">
                <CustomColumns columns={[1, 3]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.birthday')}</UserContentLabel>
                  <DatePicker
                    selectedDate={selectedDate}
                    label=""
                    todayButtonLabel={t('pages.dashboard.createUser.form.actions.today')}
                    clearButtonLabel={t('pages.dashboard.createUser.form.actions.clear')}
                    onChangeDate={(date) => handleDateChange(date, 'birthday')}
                    language={language}
                    minDate={minimumDate}
                    error={
                      DateTools.compare(new Date(), selectedDate) === 1
                        ? t('pages.dashboard.createUser.form.rules.invalidDate')
                        : undefined
                    }
                    size="s"
                    testId="date-picker-kyc-birthday"
                  />
                </CustomColumns>
                <CustomColumns columns={[1, 3]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.birthCountry')}</UserContentLabel>
                  <Select
                    options={countryCodeOptions}
                    withSearch
                    defaultValue={user?.birthCountry}
                    noOptionsComponent={<CreateUserEmptyComponent />}
                    onChange={(value: string) => handleValueChange(value, 'birthCountry')}
                    showDelete
                    size="s"
                    testId="select-kyc-birth-country"
                  />
                </CustomColumns>
                <CustomColumns columns={[1, 3]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.placeOfBirth')}</UserContentLabel>
                  <Input
                    type="text"
                    label=""
                    {...register('placeOfBirth')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'placeOfBirth')
                    }
                    size="s"
                    testId="select-kyc-birth-place"
                  />
                </CustomColumns>
                <CustomColumns columns={[1, 3]}>
                  <UserContentLabel>{t('pages.dashboard.createUser.form.nationality')}</UserContentLabel>
                  <Select
                    options={countryCodeOptions}
                    withSearch
                    defaultValue={user?.nationality}
                    noOptionsComponent={<CreateUserEmptyComponent />}
                    onChange={(value: string) => handleValueChange(value, 'nationality')}
                    showDelete
                    size="s"
                    testId="select-kyc-nationality"
                  />
                </CustomColumns>
                <AdditonalDataLayout
                  shouldShow={!!watch('nationalityOther')}
                  label={t('pages.dashboard.createUser.form.actions.addOtherNationality')}
                >
                  <CustomColumns columns={[1, 3]}>
                    <UserContentLabel>{t('pages.dashboard.createUser.form.nationalityOther')}</UserContentLabel>
                    <Select
                      options={countryCodeOptions}
                      withSearch
                      defaultValue={user?.nationalityOther}
                      noOptionsComponent={<CreateUserEmptyComponent />}
                      onChange={(value: string) => handleValueChange(value, 'nationalityOther')}
                      showDelete
                      size="s"
                      testId="select-kyc-other-nationality"
                    />
                  </CustomColumns>
                </AdditonalDataLayout>
              </div>
            ),
          }
        : {
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.birthday'), value: user?.birthday || '/' },
                  {
                    label: t('pages.dashboard.createUser.form.birthCountry'),
                    value: user?.birthCountry ? t(`shared.countries.${user.birthCountry}`) : '/',
                  },
                  { label: t('pages.dashboard.createUser.form.placeOfBirth'), value: user.placeOfBirth || '/' },
                  {
                    label: t('pages.dashboard.createUser.form.nationalities'),
                    value:
                      [user?.nationality, user?.nationalityOther]
                        .filter(Boolean)
                        .map((nationality) => t(`shared.countries.${nationality}`))
                        .join(', ') || '/',
                  },
                ],
              },
            ],
          }),
    }),
    [
      countryCodeOptions,
      handleDateChange,
      handleInputChange,
      handleValueChange,
      isBirthAndNationalityOnEdition,
      language,
      register,
      selectedDate,
      t,
      user.birthCountry,
      user?.birthday,
      user?.nationality,
      user?.nationalityOther,
      user.placeOfBirth,
      watch,
    ],
  );

  const situation: TKycSection = useMemo(
    () => ({
      subTitle: t('pages.dashboard.createUser.form.blocks.situation'),
      ...(isSituationOnEdition
        ? {
            component: (
              <div className="flex flex-col gap-4 w-[600px]">
                <div className="flex flex-col gap-3">
                  <CustomColumns columns={[2, 3]}>
                    <div className="flex items-center gap-2">
                      <UserContentLabel>{t('pages.dashboard.createUser.form.occupation.label')}</UserContentLabel>
                      <InfoTooltip
                        tooltipInfo={t('pages.dashboard.createUser.form.occupation.tooltip')}
                        infoClassName="min-w-[240px]"
                      />
                    </div>
                    <Select
                      options={occupationCategoryOptions}
                      defaultValue={user.occupationCategory}
                      onChange={(value: number) => handleValueChange(value, 'occupationCategory')}
                      noOptionsComponent={<CreateUserEmptyComponent />}
                      tooltipInfo={{
                        content: t('pages.dashboard.createUser.form.occupation.tooltip'),
                        className: 'min-w-[240px]',
                      }}
                      withSearch
                      showDelete={!hasPersonalSituation}
                      size="s"
                      testId="select-kyc-occupation"
                    />
                  </CustomColumns>
                  <CustomColumns columns={[2, 3]}>
                    <div className="flex items-center gap-2">
                      <UserContentLabel>
                        {t('pages.dashboard.createUser.form.personalAssetsEstimated.label')}
                      </UserContentLabel>
                      <InfoTooltip
                        tooltipInfo={t('pages.dashboard.createUser.form.personalAssetsEstimated.tooltip')}
                        infoClassName="min-w-[240px]"
                      />
                    </div>
                    <Select
                      options={personalAssetRanges}
                      defaultValue={user.personalAssetsRange}
                      onChange={(value: number) => handleValueChange(value, 'personalAssetsRange')}
                      noOptionsComponent={<CreateUserEmptyComponent />}
                      withSearch
                      tooltipInfo={{
                        content: t('pages.dashboard.createUser.form.personalAssetsEstimated.tooltip'),
                        className: 'min-w-[240px]',
                      }}
                      showDelete={!hasPersonalSituation}
                      size="s"
                      testId="select-kyc-personal-assets"
                    />
                  </CustomColumns>
                  <CustomColumns columns={[2, 3]}>
                    <UserContentLabel>{t('pages.dashboard.createUser.form.netMontlyIncome.label')}</UserContentLabel>
                    <Select
                      options={monthlyIncomeRangeOptions}
                      defaultValue={user.monthlyIncomeRange}
                      onChange={(value: number) => handleValueChange(value, 'monthlyIncomeRange')}
                      noOptionsComponent={<CreateUserEmptyComponent />}
                      withSearch
                      showDelete={!hasPersonalSituation}
                      size="s"
                      testId="select-kyc-monthly-income"
                    />
                  </CustomColumns>
                </div>
                <Divider thickness="200" key={`section-divider-personal-situation`} />
                <div className="flex flex-col gap-3">
                  <CustomColumns columns={[1, 1]}>
                    <RadioList
                      label={t('pages.dashboard.createUser.form.legalAnnualTurnOver')}
                      options={legalAnnualTurnOverOptions}
                      value={legalAnnualTurnOverWatch}
                      onChange={(value: string) => handleValueChange(value, 'legalAnnualTurnOver')}
                      className="pt-2 gap-3"
                    />
                    <RadioList
                      label={t('pages.dashboard.createUser.form.legalNetIncomeRanges.title')}
                      options={legalNetIncomeRangeOptions}
                      value={legalNetIncomeRangeWatch}
                      onChange={(value: string) => handleValueChange(value, 'legalNetIncomeRange')}
                      className="pt-2 gap-3"
                    />
                  </CustomColumns>
                  <CustomColumns columns={[1, 2]}>
                    <UserContentLabel>{t('pages.dashboard.createUser.form.legalNumberOfEmployee')}</UserContentLabel>
                    <Select
                      options={legalNumberOfEmployeeRangeOptions}
                      defaultValue={user?.legalNumberOfEmployeeRange}
                      noOptionsComponent={<CreateUserEmptyComponent />}
                      onChange={(value: string) => handleValueChange(value, 'legalNumberOfEmployeeRange')}
                      showDelete
                      size="s"
                      testId="select-kyc-legal-number-employee"
                    />
                  </CustomColumns>
                </div>

                <Divider thickness="200" key={`section-divider-leagl-situation`} />
                <SanctionEmbargoQuestionaire
                  defaultValue={{
                    entitySanction: entitySanctionsQuestionnaireWatch,
                    activityOutsideEu: activityOutsideEuWatch?.toString(),
                    economicSanctions: economicSanctionsWatch?.toString(),
                    residentCountriesSanctions: residentCountriesSanctionsWatch?.toString(),
                    involvedSanctions: involvedSanctionsWatch?.toString(),
                  }}
                  handleValueChange={handleValueChange}
                  size="s"
                />
              </div>
            ),
          }
        : {
            categories: [
              {
                ...(hasPersonalSituation
                  ? {
                      component: (
                        <PersonalSituationSummary
                          occupationCategory={user.occupationCategory}
                          monthlyIncomeRange={user.monthlyIncomeRange}
                          personalAssetsRange={user.personalAssetsRange}
                        />
                      ),
                    }
                  : {
                      items: [
                        {
                          label: t('pages.currentProfiles.profile.financial.occupationType'),
                          value: user?.occupationType
                            ? t(`pages.currentProfiles.profile.financial.occupationTypes.${user.occupationType}`)
                            : '/',
                        },
                        {
                          label: t('pages.dashboard.createUser.form.personalAssets.title'),
                          value: user?.personalAssets
                            ? t(`pages.dashboard.createUser.form.personalAssets.${user.personalAssets}`)
                            : '/',
                        },
                        {
                          label: t('pages.dashboard.createUser.form.incomeRange.title'),
                          value: user?.incomeRange
                            ? t(`pages.dashboard.createUser.form.incomeRange.${user.incomeRange}`)
                            : '/',
                        },
                      ],
                    }),
              },
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.legalAnnualTurnOver'),
                    value: user.legalAnnualTurnOver
                      ? t(`pages.dashboard.createUser.form.legalAnnualTurnOvers.${user.legalAnnualTurnOver}`)
                      : '/',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.legalNetIncomeRanges.title'),
                    value: user.legalNetIncomeRange
                      ? t(`pages.dashboard.createUser.form.legalNetIncomeRanges.${user.legalNetIncomeRange}`)
                      : '/',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.legalNumberOfEmployee'),
                    value: user?.legalNumberOfEmployeeRange || '/',
                  },
                ],
              },
              {
                component: (
                  <SanctionEmbargoSummary
                    entitySanctionsQuestionnaire={user?.entitySanctionsQuestionnaire}
                    activityOutsideEu={user?.activityOutsideEu}
                    economicSanctions={user?.economicSanctions}
                    residentCountriesSanctions={user?.residentCountriesSanctions}
                    involvedSanctions={user?.involvedSanctions}
                  />
                ),
              },
            ],
          }),
    }),
    [
      t,
      isSituationOnEdition,
      occupationCategoryOptions,
      user.occupationCategory,
      user.personalAssetsRange,
      user.monthlyIncomeRange,
      user?.legalNumberOfEmployeeRange,
      user.personalAssets,
      user.incomeRange,
      user.occupationType,
      user.legalAnnualTurnOver,
      user.legalNetIncomeRange,
      user?.entitySanctionsQuestionnaire,
      user?.activityOutsideEu,
      user?.economicSanctions,
      user?.residentCountriesSanctions,
      user?.involvedSanctions,
      hasPersonalSituation,
      personalAssetRanges,
      monthlyIncomeRangeOptions,
      legalAnnualTurnOverOptions,
      legalAnnualTurnOverWatch,
      legalNetIncomeRangeOptions,
      legalNetIncomeRangeWatch,
      legalNumberOfEmployeeRangeOptions,
      entitySanctionsQuestionnaireWatch,
      activityOutsideEuWatch,
      economicSanctionsWatch,
      residentCountriesSanctionsWatch,
      involvedSanctionsWatch,
      handleValueChange,
    ],
  );

  return {
    isBirthAndNationalityInvalid,
    birthNationality,
    situation,
    isLoading,
    onUpdateUser,
    onCancelEdition,
  };
}
